import * as d3 from 'd3';
import {groupBy, last, max, mean, min, throttle} from 'lodash';
import React, {ReactNode} from 'react';

import {AllColumns, Source, sectionSourceOf} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';
import {Dims} from 'src/lib/commonTypes';
import {ForecastTimeSeriesById, ObservationTimeSeriesById} from 'src/lib/dataUtils';
import {
  closestForecastTimeWithinDay,
  formatDateShortFull,
  getLatestForecastTime,
} from 'src/lib/dates';
import {ColumnType} from 'src/lib/sourceConfigs';

import {XScale, YScale, defaultTooltipFormat} from './D3Axis';
import {VizConfigItem} from './Viz/config';

export type TooltipItem = {
  y: YScale;
  column: AllColumns;
  config: VizConfigItem;
};

const MAX_DISTANCE = 20;

type Props = {
  graphDims: Dims;
  x: XScale;
  xOffset: number;
  yOffset: number;
  forecastTooltipItems: TooltipItem[];
  observationTooltipItems: TooltipItem[];
  observationTimeSeriesById: ObservationTimeSeriesById;
  forecastTimeSeriesById: ForecastTimeSeriesById;
  timeZone: string;
  snapToX: boolean;
  mouseDate: MouseDateState['mouseDate'];
  setMouseDate: MouseDateState['setMouseDate'];
  showFlag?: boolean;
};

const FONT_SIZE = 10;
const ITEM_SIZE = 16;
const TAB_BORDER_RADIUS = 4;
const bisectDate = d3.bisector((d: Datum) => d.date).left;
const AVERAGE_AFTER_FORECAST_COUNT = 5;

type Datum = {
  displayName: string;
  date: Date;
  value: number | null;
  unit: string;
  columnType: ColumnType;
  issueTime?: Date;
  initializationTime?: Date;
  y: YScale;
  icon: JSX.Element;
};

function makeGroupHeader(d: Datum, timeZone: string, isFlowForecastGraph: boolean) {
  if (d.issueTime && d.initializationTime) {
    if (isFlowForecastGraph) {
      return `Initialized ${formatDateShortFull(
        d.initializationTime,
        timeZone
      )}\n Issued ${formatDateShortFull(d.issueTime, timeZone)}`;
    } else {
      return `Initialized ${formatDateShortFull(d.initializationTime, timeZone)}`;
    }
  }
  return 'Observed';
}

function getUnitDisplay(unit: string | undefined) {
  return unit === 'dimensionless' || !unit ? '' : unit;
}

export type MouseDateState = {
  mouseDate: Date | null;
  setMouseDate: (date: Date | null) => void;
};

export const MouseDateContext = React.createContext<MouseDateState>({
  mouseDate: null,
  setMouseDate: () => {},
});

export const MouseDateProvider: React.FunctionComponent<{
  children: ReactNode;
}> = ({children}) => {
  const [mouseDate, setMouseDate] = React.useState<MouseDateState['mouseDate']>(null);

  return (
    <MouseDateContext.Provider value={{mouseDate, setMouseDate}}>
      {children}
    </MouseDateContext.Provider>
  );
};

export function UncontrolledTooltip(props: Omit<Props, 'mouseDate' | 'setMouseDate'>) {
  const {mouseDate, setMouseDate} = React.useContext(MouseDateContext);
  return <Tooltip {...props} mouseDate={mouseDate} setMouseDate={setMouseDate} />;
}

export default function Tooltip({
  graphDims,
  x,
  xOffset,
  yOffset,
  forecastTooltipItems,
  observationTooltipItems,
  observationTimeSeriesById,
  forecastTimeSeriesById,
  timeZone,
  snapToX = false,
  mouseDate,
  setMouseDate,
  showFlag = true,
}: Props) {
  const {forecastsLen, forecastsDates, isFlowForecastGraph} = React.useMemo(() => {
    const sources = Object.keys(forecastTimeSeriesById);

    const isFlowForecastGraph = Object.values(forecastTimeSeriesById).some((ts) =>
      [
        Source.HydroForecastShortTerm,
        Source.HydroForecastShortTerm2,
        Source.HydroForecastShortTerm3,
        Source.HydroForecastSeasonal,
        Source.HydroForecastSeasonalAllTraces,
      ].includes(sectionSourceOf(ts['query'].source))
    );
    if (sources.length > 0) {
      const forecasts = forecastTimeSeriesById[sources[0]].forecasts;
      return {
        forecastsLen: forecasts.length,
        forecastsDates: forecasts.map((f) => ({
          issueTime: f.issueTime,
          initializationTime: f.initializationTime,
        })),
        isFlowForecastGraph: isFlowForecastGraph,
      };
    }

    return {forecastsLen: 0, forecastsDates: [], isFlowForecastGraph: false};
  }, [forecastTimeSeriesById]);

  const dataList = React.useMemo(() => {
    return [
      ...forecastTooltipItems.flatMap((item) => {
        const ts = forecastTimeSeriesById[item.config.id];
        return ts.forecasts
          .filter((f) => f.validTimes.length)
          .map((f, f_i) => {
            const validTimes = f.validTimes;
            const dataValues = f.data[item.column];
            return validTimes.map((d, i) => ({
              displayName: item.config.showColumnInTooltipDisplayName
                ? `${item.config.displayName} (${item.column})`
                : item.config.displayName,
              date: d,
              issueTime: f.issueTime,
              initializationTime: f.initializationTime,
              value: dataValues ? dataValues[i] : null,
              unit: getUnitDisplay(ts.variables[item.column]?.unit),
              columnType: ColumnType.FORECAST,
              y: item.y,
              icon: item.config.legend.makeIcon(f_i, ts.forecasts.length),
            }));
          });
      }),
      ...observationTooltipItems.map((item) => {
        const ts = observationTimeSeriesById[item.config.id];
        return ts.timestamps.map((t, i) => {
          return {
            displayName: item.config.showColumnInTooltipDisplayName
              ? `${item.config.displayName} (${item.column})`
              : item.config.displayName,
            date: t,
            value: ts.data[item.column][i],
            unit: getUnitDisplay(ts.variables[item.column]?.unit),
            columnType: ColumnType.OBSERVED,
            y: item.y,
            icon: item.config.legend.makeIcon(0, 0),
          };
        });
      }),
    ];
  }, [
    observationTooltipItems,
    forecastTooltipItems,
    observationTimeSeriesById,
    forecastTimeSeriesById,
  ]);

  const closestDatums: Datum[] = React.useMemo(() => {
    const datums: Datum[] = [];

    if (showFlag && mouseDate) {
      dataList
        .filter((data) => {
          if (!data || !data.length) {
            return false;
          }
          const type = data[0].columnType;
          if (type === ColumnType.OBSERVED) {
            return mouseDate.getTime() <= data[data.length - 1].date.getTime();
          } else {
            return mouseDate.getTime() >= data[0].date.getTime();
          }
        })
        .forEach((data) => {
          const i = bisectDate(data, mouseDate);
          const d0 = data[i - 1];
          const d1 = data[i];
          if (d0 && d1) {
            datums.push(
              mouseDate.getTime() - d0.date.getTime() > d1.date.getTime() - mouseDate.getTime()
                ? d1
                : d0
            );
          } else if (d0 && Math.abs(x(d0.date) - x(mouseDate)) < MAX_DISTANCE) {
            datums.push(d0);
          } else if (!d0 && d1 && mouseDate.getTime() === d1.date.getTime()) {
            datums.push(d1);
          }
        });
    }

    return datums;
  }, [dataList, showFlag, mouseDate, x]);

  const firstDatum = closestDatums[0];
  let closestDate: Date | null = null;
  if (firstDatum) {
    closestDate = firstDatum.date;
  }

  let cursorDate = mouseDate;
  if (snapToX && closestDate) {
    cursorDate = closestDate;
  }

  let tooltipContent: JSX.Element | null = null;
  const contentConfig = {
    closestDatums: closestDatums,
    cursorDate,
    firstDatum,
    timeZone,
    forecastsLen,
    forecastsDates,
    isFlowForecastGraph,
  };

  if (showFlag) {
    if (forecastsLen < 2) {
      tooltipContent = <SingleForecast {...contentConfig} />;
    } else if (forecastsLen > AVERAGE_AFTER_FORECAST_COUNT) {
      tooltipContent = <ManyForecasts {...contentConfig} />;
    } else {
      tooltipContent = <FewForecasts {...contentConfig} />;
    }
  }

  // Calculate tooltip positioning
  const getTooltipPosition = () => {
    if (!cursorDate) return {};

    const cursorX = x(cursorDate);
    const estimatedTooltipWidth = 250; // Conservative estimate
    const margin = 10; // Minimum margin from edges
    const spaceOnLeft = cursorX - margin;

    // Prefer rendering tooltip on the left side of the cursor when
    // there is enough space
    if (spaceOnLeft >= estimatedTooltipWidth) {
      return {right: graphDims[0] - cursorX + xOffset * 2};
    } else {
      return {left: cursorX + xOffset * 2};
    }
  };

  return (
    <div className="absolute">
      <svg width={graphDims[0] + xOffset * 2} height={graphDims[1] + yOffset * 2}>
        <g transform={`translate(${xOffset}, ${yOffset})`}>
          {/* This draws a vertical line on the mouse date */}
          {cursorDate && (
            <line
              x1={x(cursorDate)}
              x2={x(cursorDate)}
              y1={0}
              y2={graphDims[1]}
              stroke={COLORS.AXIS}
              strokeDasharray="2"
            />
          )}
          {closestDatums.map((d, i) => {
            const {date, value, y} = d!;
            if (value != null) {
              const xPos = x(date);
              const yPos = y(value);
              return (
                <g key={i}>
                  {/* This draws a little x on the datum */}
                  <line
                    x1={xPos - 3}
                    x2={xPos + 3}
                    y1={yPos - 3}
                    y2={yPos + 3}
                    stroke="black"
                    strokeWidth={0.5}
                  />
                  <line
                    x1={xPos + 3}
                    x2={xPos - 3}
                    y1={yPos - 3}
                    y2={yPos + 3}
                    stroke="black"
                    strokeWidth={0.5}
                  />
                </g>
              );
            } else {
              return null;
            }
          })}
          <rect
            className="pointer-events-auto cursor-crosshair"
            fill="transparent"
            width={graphDims[0]}
            height={graphDims[1]}
            onMouseMove={throttle((e) => {
              const mouse = d3.pointer(e);
              const mouseDate = x.invert(mouse[0]);
              // Round date to nearest hour
              mouseDate.setHours(mouseDate.getHours() + Math.round(mouseDate.getMinutes() / 60));
              mouseDate.setMinutes(0, 0, 0); // Resets also seconds and milliseconds
              setMouseDate(mouseDate);
            }, 50)}
            onMouseLeave={() => {
              setMouseDate(null);
            }}
          />
        </g>
      </svg>
      {closestDate && cursorDate && showFlag && (
        <div
          className="rounded shadow-sm absolute z-50 border border-gray-300 pointer-events-none"
          style={{
            top: 10,
            background: COLORS.AXIS_BG,
            ...getTooltipPosition(),
          }}
        >
          <div
            className="text-center border-b border-gray-300 px-1.5 py-0.5"
            style={{fontSize: FONT_SIZE}}
          >
            <p>{formatDateShortFull(cursorDate, timeZone)}</p>
          </div>
          <div>{tooltipContent}</div>
          <div className="p-1 italic" style={{fontSize: FONT_SIZE}}>
            Hold “F” to hide cursor info
          </div>
        </div>
      )}
    </div>
  );
}

type ContentProps = {
  closestDatums: Datum[];
  firstDatum?: Datum | null;
  cursorDate: MouseDateState['mouseDate'];
  timeZone: string;
  forecastsLen: number;
  forecastsDates: {initializationTime: Date; issueTime: Date}[];
  isFlowForecastGraph: boolean;
};

/**
 * This is the tooltip that will be shown when there is
 * only one forecast selected.
 */
function SingleForecast({
  closestDatums,
  firstDatum,
  cursorDate,
  timeZone,
  forecastsLen,
  forecastsDates,
  isFlowForecastGraph,
}: ContentProps): JSX.Element {
  return (
    <div>
      <SourceList
        data={Object.entries(
          groupBy(closestDatums, (d) => makeGroupHeader(d!, timeZone, isFlowForecastGraph))
        ).filter(([_header, datums]) => datums.some((d) => d!.value != null))}
      />
    </div>
  );
}

/**
 * This is the tooltip that will be shown when there are
 * between 2 and AVERAGE_AFTER_FORECAST_COUNT (included)
 * forecasts selected.
 */
function FewForecasts({
  closestDatums,
  firstDatum,
  cursorDate,
  timeZone,
  forecastsLen,
  forecastsDates,
  isFlowForecastGraph,
}: ContentProps): JSX.Element {
  const A: number = 'A'.charCodeAt(0);

  const data: {[name: string]: Datum[]} = groupBy(closestDatums, 'displayName');

  const hasForecasts: boolean = !!Object.entries(data).find(([_, datums]) =>
    datums.find((d) => d.columnType === ColumnType.FORECAST)
  );

  let forecastTable: JSX.Element = <></>;

  if (hasForecasts) {
    const legends: JSX.Element[] = [];

    const forecastRows = Object.entries(data).filter(
      ([, datums]) => datums.length && datums[0].columnType === ColumnType.FORECAST
    );
    const newestRun = getLatestForecastTime(forecastsDates);
    const overlappingForecastDates = columnsWithValues(
      forecastsDates,
      forecastRows.map(([, datums]) => datums)
    );

    const headers: JSX.Element[] = [];
    for (let i = 0; i < overlappingForecastDates.length; i++) {
      // Flow forecast graphs should show both the issue time and the initialization time.
      // Weather forecast graphs should show only the initialization time.
      const date = isFlowForecastGraph
        ? `Initialized ${formatDateShortFull(
            overlappingForecastDates[i].initializationTime,
            timeZone
          )}, Issued ${formatDateShortFull(overlappingForecastDates[i].issueTime, timeZone)}`
        : `Initialized ${formatDateShortFull(
            overlappingForecastDates[i].initializationTime,
            timeZone
          )}`;
      legends.push(
        <li>
          <span className="font-bold">{String.fromCharCode(A + i)}:</span> {date}
        </li>
      );
      headers.push(<th>{String.fromCharCode(A + i)}</th>);
    }

    const forecastingSources: JSX.Element[] = forecastRows.map(([source, datums], rowIndex) => {
      return (
        <tr key={`source-${rowIndex}`} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
          <td className="px-2">{source}</td>
          {Array.from({length: overlappingForecastDates.length}, (_, colIndex) => {
            const matchingDatum = findDatumForColumn(
              datums,
              overlappingForecastDates,
              colIndex,
              newestRun
            );
            return matchingDatum ? (
              <td key={`source-datum-${colIndex}`} className="px-2">
                {defaultTooltipFormat(matchingDatum.value!).toLocaleString()} {matchingDatum.unit}
              </td>
            ) : (
              <td key={`source-datum-${colIndex}`} className="px-2"></td>
            );
          })}
        </tr>
      );
    });

    forecastTable = (
      <div>
        <b>Forecasts</b>

        <ul>{legends}</ul>

        <table className="w-full my-2">
          <thead>
            <tr>
              <th></th>
              {headers}
            </tr>
          </thead>

          <tbody>{forecastingSources}</tbody>
        </table>
      </div>
    );
  }

  return <MultiForecastsTable forecastTable={forecastTable} data={data} />;
}

/**
 * This is the tooltip that will be shown when there are more
 * than AVERAGE_AFTER_FORECAST_COUNT forecasts selected.
 */
function ManyForecasts({
  closestDatums,
  firstDatum,
  cursorDate,
  timeZone,
  forecastsLen,
  forecastsDates,
  isFlowForecastGraph,
}: ContentProps): JSX.Element {
  const data: {[name: string]: Datum[]} = groupBy(closestDatums, 'displayName');

  const hasForecasts: boolean = !!Object.entries(data).find(([_, datums]) =>
    datums.find((d) => d.columnType === ColumnType.FORECAST)
  );

  let forecastTable: JSX.Element = <></>;

  if (hasForecasts) {
    const forecastingSources: JSX.Element[] = Object.keys(data)
      .filter((source) => data[source].length && data[source][0].columnType === ColumnType.FORECAST)
      .map((source, i) => {
        const datums: Datum[] = data[source];
        const values = datums.map((d) => d.value).filter((d) => d !== undefined && d !== null);

        return (
          <tr key={`source-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-100'}>
            <td className="px-2">{source}</td>
            {values.length > 0 &&
              [min(values), mean(values), max(values), last(values)].map((value, j) => (
                <td key={`values-${j}`} className="px-2">
                  {defaultTooltipFormat(value!).toLocaleString()} {datums[0].unit}
                </td>
              ))}
          </tr>
        );
      });

    forecastTable = (
      <div>
        <b>Forecasts</b>

        <table className="w-full my-2">
          <thead>
            <tr>
              <th></th>
              <th>Min</th>
              <th>Mean</th>
              <th>Max</th>
              <th>Latest</th>
            </tr>
          </thead>

          <tbody>{forecastingSources}</tbody>
        </table>
      </div>
    );
  }

  return <MultiForecastsTable forecastTable={forecastTable} data={data} />;
}

function MultiForecastsTable({
  forecastTable,
  data,
}: {
  forecastTable: JSX.Element;
  data: {[name: string]: Datum[]};
}): JSX.Element {
  const observationSources: [string, Datum[]][] = [];

  Object.entries(data)
    .filter(([header, datums]) => datums.length && datums[0].columnType === ColumnType.OBSERVED)
    .forEach(([_, datums]) => {
      if (observationSources.length === 0) observationSources.push(['Observed', []]);

      observationSources[0][1] = [...observationSources[0][1], ...datums];
    });

  return (
    <div className="p-1" style={{fontSize: FONT_SIZE}}>
      {forecastTable}
      <SourceList data={observationSources} packLeft={true} />
    </div>
  );
}

function SourceList({
  data,
  packLeft = false,
}: {
  data: [string, Datum[]][];
  packLeft?: boolean;
}): JSX.Element {
  const justify: string = packLeft ? '' : 'justify-between';

  return (
    <>
      {data.map(([header, datums], i) => {
        return (
          <div className="p-1" key={i}>
            <p className="font-bold whitespace-pre-line" style={{fontSize: FONT_SIZE}}>
              {header}
            </p>
            {datums
              .sort((a, b) => b!.value! - a!.value!)
              .map((d, j) => {
                if (!d || d.value == null) {
                  return null;
                }
                return (
                  <div className="flex items-center" key={j}>
                    <svg
                      width={ITEM_SIZE}
                      height={ITEM_SIZE}
                      style={{borderRadius: TAB_BORDER_RADIUS}}
                    >
                      {d.icon}
                    </svg>
                    <div
                      className={`flex flex-1 items-center ${justify} ml-1`}
                      style={{fontSize: FONT_SIZE}}
                    >
                      <p>{d.displayName}</p>
                      <div className="ml-2">
                        <span className="font-mono">{defaultTooltipFormat(d.value)}</span>
                        <span> {d.unit}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        );
      })}
    </>
  );
}

const sameInit = (a: {initializationTime: Date}, b: {initializationTime: Date}) =>
  a.initializationTime.getTime() === b.initializationTime.getTime();

// The first series' runs that head a column at the hovered time: those that it, or a run of
// another series paired with it, has a value for there. The rest of its runs are not relevant at
// that time. A column stays past the first series' own horizon while a paired run has values.
export function columnsWithValues<C extends {initializationTime: Date}>(
  forecastsDates: C[],
  datumRows: {initializationTime?: Date}[][]
): C[] {
  const newestRun = getLatestForecastTime(forecastsDates);
  return forecastsDates.filter((_, colIndex) =>
    datumRows.some(
      (datums) => findDatumForColumn(datums, forecastsDates, colIndex, newestRun) !== undefined
    )
  );
}

/**
 * The datum a column shows. The columns are the first series' runs, and the other series draw
 * the run paired with each of those (pairModelRuns, nearestForecastTimes), so a column shows the
 * run paired with it the same way: the series' own run at that time, else for the newest column
 * the series' newest run, else its run nearest the column within a day. A run paired with two
 * columns shows under both, as it is drawn for both.
 */
export function findDatumForColumn<D extends {initializationTime?: Date}>(
  datums: D[],
  columns: {initializationTime: Date}[],
  colIndex: number,
  newestColumn: {initializationTime: Date} | undefined = getLatestForecastTime(columns)
): D | undefined {
  const column = columns[colIndex];
  const runs = datums.filter(
    (datum): datum is D & {initializationTime: Date} => datum.initializationTime !== undefined
  );
  return (
    runs.find((run) => sameInit(run, column)) ??
    (newestColumn && sameInit(column, newestColumn)
      ? getLatestForecastTime(runs)
      : closestForecastTimeWithinDay(column, runs))
  );
}
